import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Input, Select, DatePicker, notification } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import styled from 'styled-components'
import { API } from 'src/utils/interceptor'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import Loading from 'src/components/styled/Loading'
import {
  discardUnconfirmedUploadedInvoices,
  enrichUploadedInvoices,
  getUploadedInvoices,
  reconcileInvoiceOneClick,
} from 'src/features/NewInvoicesSlice'
import {
  autoSendReconciledInvoiceToXero,
  fetchReceiptingOrders,
} from 'src/features/ReceiptingSlice'
import { fetchReconciliationToleranceSettings } from 'src/utils/invoiceMatchToleranceApi'
import {
  applyInvoiceTotalEstimate,
  buildEnrichPayloadFromUploadFile,
  canUseInvoiceTotalEstimate,
  getAiFieldBadgeLabel,
  getInvoiceTotalEstimateFromLines,
  getMissingMandatoryUploadLabels,
  getUploadSoftWarnings,
  INVOICE_TOTAL_HELPER_TEXT,
  INVOICE_TOTAL_SOURCE,
  isLikelyDeliveryNoteUpload,
  isUploadDisabled,
  normaliseInvoiceDateInput,
} from 'src/utils/invoiceUploadHelpers'
import {
  CN_DOCUMENT_COPY,
  getCreditNoteApplySuccessCopy,
  getCreditNoteAbsolutePennies,
} from 'src/utils/creditNoteDocumentHelpers'
import { maybePromptAndCloseCreditRequestAfterCnApply } from 'src/utils/maybePromptAndCloseCreditRequestAfterCnApply'
import {
  toastPostReconcileXeroAutoSend,
  XERO_AUTO_SEND_PARTIAL_WAIT_COPY,
} from 'src/utils/xeroAutoSendCopy'
import { shouldAutoSendAfterReconcile } from 'src/utils/xeroAutoSendBill'
import {
  applyOcrSuggestionsKeepingOrderContext,
  buildGoodsReceiptingAttachTotalFields,
  buildGoodsReceiptingDifferenceSummary,
  getOrderGrossTotalPenniesFromLines,
  resolveGoodsReceiptingTolerancePennies,
} from 'src/utils/goodsReceiptingAttachHelpers'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'

const { Option } = Select

const PREVIEW_ZOOM_MIN = 1
const PREVIEW_ZOOM_MAX = 4
const PREVIEW_ZOOM_STEP = 0.25

function clampPreviewZoom(value) {
  return Math.min(
    PREVIEW_ZOOM_MAX,
    Math.max(PREVIEW_ZOOM_MIN, Math.round(value * 100) / 100),
  )
}

function FieldBadge({ label }) {
  if (!label) return null
  return <AiBadge data-edited={label === 'Edited'}>{label}</AiBadge>
}

/**
 * Confirm step for Goods Receipting local uploads.
 * Prefills order context, runs OCR, supports line-item estimate + discard,
 * shows mismatch flags / order-vs-invoice difference, and can reconcile
 * immediately when within tolerance.
 */
function GoodsReceiptingAttachConfirmModal({
  open,
  order,
  orderLines = [],
  files = [],
  venueId,
  filterDates,
  appendToExisting = false,
  onClose,
  onAttached,
}) {
  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue?.venue)
  const user = useSelector(state => state.auth)
  const username = user?.account?.name
  const isAdmin = user?.account?.isAdmin
  const venueTitle = venue?.title

  const [selectedFiles, setSelectedFiles] = useState([])
  const [previewZoomByKey, setPreviewZoomByKey] = useState({})
  const [ocrLoading, setOcrLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [discardLoading, setDiscardLoading] = useState(false)
  const [ocrBanner, setOcrBanner] = useState('')
  const [toleranceSettings, setToleranceSettings] = useState(null)
  const [reconcileAfterAttach, setReconcileAfterAttach] = useState(false)
  const [ocrStartedForOpen, setOcrStartedForOpen] = useState(false)

  const dateFormat = 'DD/MM/YYYY'
  const resolvedOrderIds = Array.isArray(order?.orderIds)
    ? order.orderIds.filter(Boolean)
    : order?._id
    ? [order._id]
    : []

  const tolerancePennies = useMemo(
    () =>
      resolveGoodsReceiptingTolerancePennies({
        venue,
        venueSupplier: order?.venueSupplier,
        settings: toleranceSettings,
      }),
    [venue, order?.venueSupplier, toleranceSettings],
  )

  const orderGrossPennies = useMemo(
    () => getOrderGrossTotalPenniesFromLines(orderLines),
    [orderLines],
  )

  useEffect(() => {
    if (!open) {
      setOcrStartedForOpen(false)
      return
    }
    setSelectedFiles(files || [])
    setPreviewZoomByKey({})
    setOcrBanner('')
    setReconcileAfterAttach(false)

    let cancelled = false
    ;(async () => {
      if (!venueId) return
      try {
        const settings = await fetchReconciliationToleranceSettings(venueId)
        if (!cancelled) setToleranceSettings(settings)
      } catch (error) {
        console.error('Could not load match tolerance for GR attach', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, files, venueId])

  useEffect(() => {
    if (!open || !files?.length || ocrStartedForOpen) return
    setOcrStartedForOpen(true)
    runUploadAndOcr(files)
  }, [open, ocrStartedForOpen, files])

  const runUploadAndOcr = async uploadFiles => {
    setOcrLoading(true)
    setOcrBanner('Uploading and reading the invoice…')
    try {
      const uploadBody = {
        venue: venueId,
        venueTitle,
        images: uploadFiles.map(file => ({
          filename: file.filename,
          originalFilename: file.filename,
          image: file.image,
          documentType: file.documentType || 'invoice',
          comment: '',
          purchaseOrderNumber: file.purchaseOrderNumber || '',
        })),
        isDashboardUpload: true,
        username,
        userType: isAdmin ? 'Admin' : 'Customer',
      }

      const uploadResponse = await API.post(
        'api',
        `venue/${venueId}/invoices_photos_to_s3`,
        { body: uploadBody },
      )

      if (!uploadResponse?.success) {
        throw new Error(uploadResponse?.message || 'Upload failed')
      }
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
        viaServer: true,
      })

      const newInvoices = uploadResponse.newInvoices || []
      if (venueId && newInvoices.length > 0) {
        emitGrowthEngineGuidedLaneProgress(
          venueId,
          GROWTH_ENGINE_GUIDED_LANE_REASONS.INVOICE_UPLOADED,
        )
      }
      const withOcr = await Promise.all(
        uploadFiles.map(async (file, index) => {
          const created = newInvoices[index]
          const invoiceId = created?._id ? String(created._id) : null
          if (!invoiceId) {
            return {
              ...file,
              ocrStatus: 'failed',
              ocrError: 'Upload did not return an invoice id',
              ocrSuggested: null,
              ocrWarnings: ['upload_missing_id'],
            }
          }

          try {
            const ocrResponse = await API.post(
              'api2',
              `invoices/${invoiceId}/ocr`,
              { body: {} },
            )
            return applyOcrSuggestionsKeepingOrderContext(
              { ...file, invoiceId },
              {
                ...(ocrResponse?.data || {}),
                invoiceId,
              },
              [],
            )
          } catch (error) {
            console.error('Invoice OCR failed:', error)
            return {
              ...file,
              invoiceId,
              ocrStatus: 'failed',
              ocrSuggested: null,
              ocrWarnings: ['ocr_request_failed'],
              ocrError:
                error?.response?.data?.message ||
                error?.message ||
                'Invoice OCR failed',
              grOcrMismatches: [],
            }
          }
        }),
      )

      setSelectedFiles(withOcr)
      const anySuggested = withOcr.some(file => file.ocrStatus === 'suggested')
      setOcrBanner(
        anySuggested
          ? 'We’ve read these details from the invoice. Order PO and supplier are locked from this Goods Receipting row — check any mismatch flags below.'
          : 'We couldn’t suggest the details—please enter invoice number, total and date below.',
      )
    } catch (error) {
      console.error(error)
      notification.error({
        message: 'Upload failed',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
      onClose?.()
    } finally {
      setOcrLoading(false)
    }
  }

  const discardUnconfirmedUploads = async filesToDiscard => {
    await discardUnconfirmedUploadedInvoices(filesToDiscard || [], venueId)
  }

  const resetAndClose = () => {
    setSelectedFiles([])
    setOcrBanner('')
    setPreviewZoomByKey({})
    setReconcileAfterAttach(false)
    onClose?.()
  }

  const handleCancel = async () => {
    if (ocrLoading || confirmLoading || discardLoading) return
    const previousBanner = ocrBanner
    setDiscardLoading(true)
    setOcrBanner('Cancelling upload…')
    try {
      const count = selectedFiles.length
      await discardUnconfirmedUploads(selectedFiles)
      if (count > 0) {
        notification.success({
          message: count === 1 ? 'Upload discarded' : 'Uploads discarded',
          description:
            count === 1
              ? 'The unconfirmed upload was removed.'
              : `${count} unconfirmed uploads were removed.`,
        })
      }
      resetAndClose()
    } catch (error) {
      console.error('Could not discard GR invoice upload:', error)
      notification.error({
        message: 'Could not cancel upload',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
      setOcrBanner(previousBanner)
    } finally {
      setDiscardLoading(false)
    }
  }

  const handleRemoveFile = async index => {
    const fileToRemove = selectedFiles[index]
    const previousBanner = ocrBanner
    setDiscardLoading(true)
    setOcrBanner('Removing upload…')
    try {
      await discardUnconfirmedUploads(fileToRemove ? [fileToRemove] : [])
    } catch (error) {
      notification.error({
        message: 'Could not remove upload',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
      setDiscardLoading(false)
      return
    }

    const updated = [...selectedFiles]
    updated.splice(index, 1)
    setSelectedFiles(updated)
    if (!updated.length) {
      notification.success({
        message: 'Upload removed',
        description: 'No uploads left in this session.',
      })
      resetAndClose()
    } else {
      notification.success({
        message: 'Upload removed',
        description: 'Review the remaining uploads in the session.',
      })
      setOcrBanner(previousBanner)
    }
    setDiscardLoading(false)
  }

  const updateFilesValues = (number, field, value) => {
    setSelectedFiles(curr =>
      curr.map((item, index) =>
        number === index ? { ...item, [field]: value } : item,
      ),
    )
  }

  const updateInvoiceTotalInput = (number, value) => {
    setSelectedFiles(curr =>
      curr.map((item, index) =>
        number === index
          ? {
              ...item,
              invoiceTotalInput: value,
              invoiceTotalSource: INVOICE_TOTAL_SOURCE.MANUAL,
            }
          : item,
      ),
    )
  }

  const useLineItemEstimate = number => {
    setSelectedFiles(curr =>
      curr.map((item, index) =>
        number === index ? applyInvoiceTotalEstimate(item) : item,
      ),
    )
  }

  const getPreviewZoom = index =>
    clampPreviewZoom(previewZoomByKey[index] || PREVIEW_ZOOM_MIN)

  const adjustPreviewZoom = (index, delta) => {
    setPreviewZoomByKey(prev => ({
      ...prev,
      [index]: clampPreviewZoom((prev[index] || PREVIEW_ZOOM_MIN) + delta),
    }))
  }

  const primaryFile = selectedFiles[0]
  const differenceSummary = useMemo(
    () =>
      buildGoodsReceiptingDifferenceSummary({
        invoiceTotalInput: primaryFile?.invoiceTotalInput || '',
        orderGrossPennies,
        tolerancePennies,
        documentType: primaryFile?.documentType || 'invoice',
      }),
    [
      primaryFile?.invoiceTotalInput,
      primaryFile?.documentType,
      orderGrossPennies,
      tolerancePennies,
    ],
  )

  const canReconcile =
    selectedFiles.length === 1 &&
    differenceSummary.canOfferReconcile &&
    !differenceSummary.isCreditNote
  const isCreditNoteAttach = Boolean(differenceSummary.isCreditNote)

  const busy = ocrLoading || confirmLoading || discardLoading
  const confirmDisabled = isUploadDisabled(selectedFiles) || busy

  const attachConfirmedInvoices = async ({ reconcile }) => {
    if (isUploadDisabled(selectedFiles)) return
    if (!resolvedOrderIds.length) {
      notification.error({
        message: 'Unable to attach invoice',
        description:
          'This order is missing order IDs. Please refresh and try again.',
      })
      return
    }

    setConfirmLoading(true)
    setReconcileAfterAttach(Boolean(reconcile))
    try {
      await enrichUploadedInvoices(
        selectedFiles.map(file => ({
          invoiceId: file.invoiceId,
          body: {
            ...buildEnrichPayloadFromUploadFile(file),
            supplierConfirmedByUser: true,
          },
        })),
      )

      const invoiceIds = selectedFiles
        .map(file => file.invoiceId)
        .filter(Boolean)
      const primary = selectedFiles[0]
      const totalFields = buildGoodsReceiptingAttachTotalFields(primary)

      const attachResponse = await API.put('api2', 'orders/attachInvoice', {
        body: {
          orderIds: resolvedOrderIds,
          invoiceKeys: [],
          invoiceFiles: [],
          invoiceKey: '',
          invoiceKeyNames: [],
          selectedInvoices: invoiceIds,
          selectedInvoice: invoiceIds[0] || '',
          venueId,
          invoiceNumber: primary.invoiceNumber,
          invoiceDate: moment(primary.invoiceDate, 'YYYY-MM-DD').toISOString(),
          appendToExisting: !!appendToExisting,
          ...totalFields,
        },
      })
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
      })

      if (reconcile && canReconcile && invoiceIds[0]) {
        let showedSentSuccess = false
        try {
          const reconcileResult = await reconcileInvoiceOneClick(
            invoiceIds[0],
            {
              purchaseOrderNumber: order?.purchaseOrderNumber || '',
            },
          )
          if (shouldAutoSendAfterReconcile(reconcileResult)) {
            try {
              const autoSendResult = await dispatch(
                autoSendReconciledInvoiceToXero({
                  venue,
                  order: {
                    ...order,
                    orderIds:
                      reconcileResult?.linked?.orderIds?.length > 0
                        ? reconcileResult.linked.orderIds
                        : resolvedOrderIds,
                    invoiceNumber:
                      order?.invoiceNumber || primary.invoiceNumber,
                  },
                  venueSupplier: order?.venueSupplier,
                  invoiceId: invoiceIds[0],
                  invoiceNumber: primary.invoiceNumber,
                }),
              )
              showedSentSuccess = toastPostReconcileXeroAutoSend({
                autoSendResult,
                venue,
                venueSupplier: order?.venueSupplier,
                order,
              }).showedSentSuccess
            } catch (xeroError) {
              // Reconcile remains successful; Xero is independently retryable.
              console.error('Post-reconcile Xero auto-send failed', xeroError)
              toastPostReconcileXeroAutoSend({
                error: xeroError,
                venue,
                venueSupplier: order?.venueSupplier,
                order,
              })
            }
          }
          if (!showedSentSuccess) {
            notification.success({
              message: 'Invoice attached and reconciled',
              description:
                reconcileResult?.invoiceFullyReconciled === false
                  ? XERO_AUTO_SEND_PARTIAL_WAIT_COPY
                  : undefined,
            })
          }
        } catch (reconcileError) {
          console.error(reconcileError)
          notification.warning({
            message: 'Invoice attached, but reconcile failed',
            description:
              reconcileError?.response?.data?.message ||
              reconcileError?.message ||
              'You can reconcile from Invoices & Credit Notes.',
          })
        }
      } else if (isCreditNoteAttach) {
        const linked =
          attachResponse?.data?.linked || attachResponse?.linked || {}
        const creditAbs =
          linked.creditPenniesAbs != null
            ? linked.creditPenniesAbs
            : getCreditNoteAbsolutePennies({
                invoiceTotal: differenceSummary.invoiceTotalPennies,
                documentType: 'creditNote',
              })
        const remaining =
          linked.remainingPennies != null
            ? linked.remainingPennies
            : orderGrossPennies != null && creditAbs != null
            ? orderGrossPennies - creditAbs
            : null
        notification.success({
          message: 'Credit applied',
          description: getCreditNoteApplySuccessCopy(creditAbs, remaining, {
            waitingForInvoice: Boolean(linked.waitingForInvoice),
          }),
        })
        try {
          await maybePromptAndCloseCreditRequestAfterCnApply({
            dispatch,
            venueId,
            purchaseOrderNumber: order?.purchaseOrderNumber || '',
            order,
          })
        } catch (bridgeError) {
          console.error(bridgeError)
        }
      } else {
        notification.success({
          message: appendToExisting
            ? 'Invoice added successfully!'
            : 'Invoice attached successfully!',
        })
      }

      if (venueId && filterDates?.start && filterDates?.end) {
        dispatch(
          fetchReceiptingOrders(
            venueId,
            moment(filterDates.start).format(dateFormat),
            moment(filterDates.end).format(dateFormat),
          ),
        )
      }
      if (venueId) {
        dispatch(getUploadedInvoices(venueId))
      }

      onAttached?.({
        invoiceIds,
        reconciled: Boolean(reconcile && canReconcile),
        creditApplied: isCreditNoteAttach,
      })
      resetAndClose()
    } catch (error) {
      console.error(error)
      notification.error({
        message: isCreditNoteAttach
          ? 'Could not apply credit note'
          : 'Could not attach invoice',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    } finally {
      setConfirmLoading(false)
      setReconcileAfterAttach(false)
    }
  }

  return (
    <Modal
      visible={open}
      width={760}
      title={
        isCreditNoteAttach
          ? `Apply credit note to ${order?.supplier?.title || ''} ${
              order?.purchaseOrderNumber || ''
            }`
          : `Confirm invoice for ${order?.supplier?.title || ''} ${
              order?.purchaseOrderNumber || ''
            }`
      }
      onCancel={handleCancel}
      destroyOnClose
      footer={
        <FooterRow>
          <Button
            onClick={handleCancel}
            disabled={busy}
            loading={discardLoading}
          >
            Cancel
          </Button>
          {isCreditNoteAttach ? (
            <Button
              type='primary'
              disabled={confirmDisabled}
              loading={confirmLoading}
              onClick={() => attachConfirmedInvoices({ reconcile: false })}
              title={CN_DOCUMENT_COPY.applyActionTooltip}
            >
              {CN_DOCUMENT_COPY.applyActionLabel}
            </Button>
          ) : (
            <>
              <Button
                type='default'
                disabled={confirmDisabled}
                loading={confirmLoading && !reconcileAfterAttach}
                onClick={() => attachConfirmedInvoices({ reconcile: false })}
              >
                Attach
              </Button>
              <Button
                type='primary'
                disabled={confirmDisabled || !canReconcile}
                loading={confirmLoading && reconcileAfterAttach}
                onClick={() => attachConfirmedInvoices({ reconcile: true })}
                title={
                  canReconcile
                    ? 'Attach and reconcile — totals are within match tolerance'
                    : 'Available when a single invoice total is within match tolerance of the order'
                }
              >
                Attach & Reconcile
              </Button>
            </>
          )}
        </FooterRow>
      }
    >
      {busy ? (
        <LoadingWrapper>
          <Loading />
          {ocrBanner && <OcrStatusText>{ocrBanner}</OcrStatusText>}
        </LoadingWrapper>
      ) : (
        <InvoicesWrapper>
          {ocrBanner && <IntroText>{ocrBanner}</IntroText>}
          <IntroText>
            Supplier and PO come from this Goods Receipting order. Confirm the
            invoice number, total (including VAT) and date from the document.
          </IntroText>

          {differenceSummary.invoiceTotalPennies != null &&
            differenceSummary.orderGrossPennies != null && (
              <DifferenceStrip data-within={differenceSummary.withinTolerance}>
                <DifferenceRow>
                  <span>Invoice total</span>
                  <strong>{differenceSummary.invoiceTotalDisplay}</strong>
                </DifferenceRow>
                <DifferenceRow>
                  <span>Order total (inc VAT)</span>
                  <strong>{differenceSummary.orderGrossDisplay}</strong>
                </DifferenceRow>
                <DifferenceRow>
                  <span>Difference</span>
                  <strong>{differenceSummary.differenceDisplay}</strong>
                </DifferenceRow>
                <DifferenceRow>
                  <span>Match tolerance</span>
                  <strong>{differenceSummary.toleranceDisplay}</strong>
                </DifferenceRow>
                <DifferenceHint>
                  {differenceSummary.isCreditNote
                    ? CN_DOCUMENT_COPY.matchingGuidance
                    : differenceSummary.withinTolerance
                    ? 'Within tolerance — you can Attach & Reconcile now.'
                    : 'Outside tolerance — attach only, then review before reconciling.'}
                </DifferenceHint>
              </DifferenceStrip>
            )}

          {selectedFiles.map((file, index) => {
            const isPdf = file.image?.base64?.startsWith(
              'data:application/pdf;base64,',
            )
            const missingLabels = getMissingMandatoryUploadLabels(file)
            const softWarnings = getUploadSoftWarnings(file)
            const deliveryNoteUpload = isLikelyDeliveryNoteUpload(file)
            const invoiceDateValue = file.invoiceDate
              ? moment(file.invoiceDate, 'YYYY-MM-DD')
              : null
            const mismatches = file.grOcrMismatches || []

            return (
              <Card key={file.invoiceId || index}>
                <FilePreview>
                  <PreviewToolbar>
                    {!isPdf && (
                      <ZoomControls>
                        <ZoomButton
                          type='default'
                          size='small'
                          disabled={getPreviewZoom(index) <= PREVIEW_ZOOM_MIN}
                          onClick={() =>
                            adjustPreviewZoom(index, -PREVIEW_ZOOM_STEP)
                          }
                          aria-label='Zoom out'
                        >
                          −
                        </ZoomButton>
                        <ZoomLevel>
                          {Math.round(getPreviewZoom(index) * 100)}%
                        </ZoomLevel>
                        <ZoomButton
                          type='default'
                          size='small'
                          disabled={getPreviewZoom(index) >= PREVIEW_ZOOM_MAX}
                          onClick={() =>
                            adjustPreviewZoom(index, PREVIEW_ZOOM_STEP)
                          }
                          aria-label='Zoom in'
                        >
                          +
                        </ZoomButton>
                      </ZoomControls>
                    )}
                    {isPdf && (
                      <OpenPdfLink
                        href={file.image.base64}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Open PDF in new tab
                      </OpenPdfLink>
                    )}
                    <CrossIcon onClick={() => handleRemoveFile(index)}>
                      <CloseOutlined
                        style={{ fontSize: '15px', color: '#fff' }}
                      />
                    </CrossIcon>
                  </PreviewToolbar>
                  {isPdf ? (
                    <PdfPreviewFrame
                      title={file.filename || 'Invoice PDF'}
                      src={file.image.base64}
                    />
                  ) : (
                    <PreviewViewport>
                      <PreviewImage
                        src={file.image.base64}
                        alt={`Invoice ${index + 1}`}
                        style={{
                          width: `${getPreviewZoom(index) * 100}%`,
                        }}
                      />
                    </PreviewViewport>
                  )}
                </FilePreview>

                {mismatches.length > 0 && (
                  <SoftWarningList>
                    {mismatches.map(warning => (
                      <SoftWarningItem key={warning.code}>
                        {warning.message}
                      </SoftWarningItem>
                    ))}
                  </SoftWarningList>
                )}

                {softWarnings.length > 0 && (
                  <SoftWarningList>
                    {softWarnings.map(warning => (
                      <SoftWarningItem key={warning.code}>
                        {warning.message}
                      </SoftWarningItem>
                    ))}
                  </SoftWarningList>
                )}

                <FormField>
                  <FieldLabel>
                    <span>
                      Supplier <RequiredMark>*</RequiredMark>
                    </span>
                  </FieldLabel>
                  <ReadOnlyValue>{file.supplierTitle || '—'}</ReadOnlyValue>
                  <GuidanceText>
                    Locked from this Goods Receipting order.
                  </GuidanceText>
                </FormField>

                <FormField>
                  <FieldLabel>
                    <span>Document type</span>
                    <FieldBadge
                      label={getAiFieldBadgeLabel(file, 'documentType')}
                    />
                  </FieldLabel>
                  <Select
                    size='large'
                    value={file.documentType || 'invoice'}
                    onChange={value =>
                      updateFilesValues(index, 'documentType', value)
                    }
                  >
                    <Option value='invoice'>Invoice</Option>
                    <Option value='creditNote'>Credit Note</Option>
                  </Select>
                  {file.documentType === 'creditNote' ? (
                    <GuidanceText>
                      {CN_DOCUMENT_COPY.uploadConfirm}
                    </GuidanceText>
                  ) : null}
                </FormField>

                <FormField>
                  <FieldLabel>
                    <span>
                      PO number <RequiredMark>*</RequiredMark>
                    </span>
                  </FieldLabel>
                  <ReadOnlyValue>
                    {file.purchaseOrderNumber || '—'}
                  </ReadOnlyValue>
                  <GuidanceText>
                    Locked from this Goods Receipting order.
                  </GuidanceText>
                </FormField>

                <FormField>
                  <FieldLabel>
                    <span>
                      Invoice / credit note number{' '}
                      <RequiredMark>*</RequiredMark>
                    </span>
                    <FieldBadge
                      label={getAiFieldBadgeLabel(file, 'invoiceNumber')}
                    />
                  </FieldLabel>
                  <Input
                    size='large'
                    placeholder='Supplier invoice number'
                    value={file.invoiceNumber || ''}
                    onChange={event =>
                      updateFilesValues(
                        index,
                        'invoiceNumber',
                        event.target.value,
                      )
                    }
                  />
                </FormField>

                <FormField>
                  <FieldLabel>
                    <span>
                      Total (including VAT)
                      {!deliveryNoteUpload && (
                        <>
                          {' '}
                          <RequiredMark>*</RequiredMark>
                        </>
                      )}
                    </span>
                    <FieldBadge
                      label={getAiFieldBadgeLabel(file, 'invoiceTotal')}
                    />
                  </FieldLabel>
                  <Input
                    size='large'
                    placeholder={
                      deliveryNoteUpload
                        ? 'Optional for delivery notes'
                        : 'e.g. 51.95'
                    }
                    value={file.invoiceTotalInput || ''}
                    onChange={event =>
                      updateInvoiceTotalInput(index, event.target.value)
                    }
                  />
                  {canUseInvoiceTotalEstimate(file) &&
                    (() => {
                      const estimate = getInvoiceTotalEstimateFromLines(file)
                      return (
                        <EstimateOffer>
                          <EstimateText>
                            No total was printed. We added up the{' '}
                            {estimate.count > 0
                              ? `${estimate.count} line ${
                                  estimate.count === 1 ? 'item' : 'items'
                                }`
                              : 'line items'}{' '}
                            to an estimated{' '}
                            <strong>£{estimate.formatted}</strong>.
                          </EstimateText>
                          <EstimateButton
                            type='primary'
                            size='small'
                            onClick={() => useLineItemEstimate(index)}
                          >
                            Use £{estimate.formatted}
                          </EstimateButton>
                        </EstimateOffer>
                      )
                    })()}
                  <GuidanceText>
                    {deliveryNoteUpload
                      ? 'Delivery notes often have no total — you can leave this blank.'
                      : INVOICE_TOTAL_HELPER_TEXT}
                  </GuidanceText>
                </FormField>

                <FormField>
                  <FieldLabel>
                    <span>
                      Invoice date <RequiredMark>*</RequiredMark>
                    </span>
                    <FieldBadge
                      label={getAiFieldBadgeLabel(file, 'invoiceDate')}
                    />
                  </FieldLabel>
                  <DatePicker
                    size='large'
                    format='DD/MM/YYYY'
                    style={{ width: '100%' }}
                    value={invoiceDateValue}
                    onChange={value =>
                      updateFilesValues(
                        index,
                        'invoiceDate',
                        normaliseInvoiceDateInput(value),
                      )
                    }
                  />
                </FormField>

                {missingLabels.length > 0 && (
                  <MissingFieldsText>
                    Still needed: {missingLabels.join(', ')}
                  </MissingFieldsText>
                )}
              </Card>
            )
          })}
        </InvoicesWrapper>
      )}
    </Modal>
  )
}

export default GoodsReceiptingAttachConfirmModal

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`

const InvoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
`

const OcrStatusText = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
`

const IntroText = styled.p`
  width: 100%;
  margin: 0 0 16px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.5;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
`

const FormField = styled.div`
  width: 100%;
`

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`

const RequiredMark = styled.span`
  color: #ff4d4f;
`

const GuidanceText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
`

const ReadOnlyValue = styled.div`
  padding: 8px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
`

const AiBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  color: ${props => (props['data-edited'] ? '#595959' : '#3f6212')};
  background: ${props => (props['data-edited'] ? '#f5f5f5' : '#f0f7da')};
  border: 1px solid ${props => (props['data-edited'] ? '#d9d9d9' : '#c6df7b')};
`

const EstimateOffer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f0f7da;
  border: 1px solid #c6df7b;
`

const EstimateText = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: #3f6212;
`

const EstimateButton = styled(Button)`
  && {
    flex-shrink: 0;
    background: #b5d922;
    border-color: #b5d922;
    color: #1f2d00;
    font-weight: 600;
  }
`

const MissingFieldsText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #cf1322;
`

const SoftWarningList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SoftWarningItem = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  color: #614700;
  font-size: 13px;
  line-height: 1.45;
`

const DifferenceStrip = styled.div`
  width: 100%;
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${props => (props['data-within'] ? '#b7eb8f' : '#ffd591')};
  background: ${props => (props['data-within'] ? '#f6ffed' : '#fff7e6')};
`

const DifferenceRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.75);
`

const DifferenceHint = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
`

const FilePreview = styled.div`
  position: relative;
  width: 100%;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
`

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 32px;
`

const ZoomControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const ZoomButton = styled(Button)`
  && {
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    font-size: 16px;
    font-weight: 600;
  }
`

const ZoomLevel = styled.span`
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
`

const OpenPdfLink = styled.a`
  font-size: 13px;
  font-weight: 500;
  color: #389e0d;
`

const PreviewViewport = styled.div`
  width: 100%;
  max-height: 360px;
  overflow: auto;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #f0f0f0;
`

const PreviewImage = styled.img`
  display: block;
  max-width: none;
  height: auto;
  margin: 0 auto;
`

const PdfPreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 360px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`

const CrossIcon = styled.div`
  background: #b5d922;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  cursor: pointer;
  flex-shrink: 0;
`
